import { mapEase } from "./ease";
import { scheduleNext } from "./timing";

export const rotateAnim = (
	element,
	{
		gsap,
		loopBehavior = "infinite",
		animatedElements,
		sporadicTimers,
		duration = 1,
		ease = "none",
		randomDelayMin = 3000, // ms
		randomDelayMax = 8000, // ms
	},
) => {
	if (!element || !gsap || !animatedElements) return null;
	if (animatedElements.has(element)) return animatedElements.get(element);

	let observer;
	let anim = null;

	const mappedEase = mapEase(ease);

	const startSingleCycle = () =>
		gsap.fromTo(
			element,
			{ rotation: 0 },
			{
				rotation: 360,
				duration,
				ease: mappedEase,
				transformOrigin: "center center",
				onComplete: () => {
					// For sporadic, schedule next cycle after this completes
					if (loopBehavior === "sporadic" && animatedElements.has(element)) {
						animatedElements.set(element, null);
						scheduleNext({
							element,
							animatedElements,
							sporadicTimers,
							startSingleCycle,
							randomDelayMin,
							randomDelayMax,
						});
					}
				},
			},
		);

	const startAnimation = () => {
		if (animatedElements.has(element) && animatedElements.get(element)) {
			animatedElements.get(element).play();
			return;
		}
		switch (loopBehavior) {
			case "once":
				anim = gsap.to(element, {
					rotation: 360,
					duration,
					repeat: 0,
					ease: mappedEase,
					transformOrigin: "center center",
				});
				break;
			case "infinite":
				anim = gsap.to(element, {
					rotation: 360,
					duration,
					repeat: -1,
					ease: mappedEase,
					transformOrigin: "center center",
				});
				break;
			case "sporadic":
				animatedElements.set(element, null);
				scheduleNext({
					element,
					animatedElements,
					sporadicTimers,
					startSingleCycle: () => startAnimation(),
					randomDelayMin,
					randomDelayMax,
				});
				return;
			default:
				anim = gsap.to(element, {
					rotation: 360,
					duration,
					repeat: 0,
					ease: mappedEase,
					transformOrigin: "center center",
				});
		}
		animatedElements.set(element, anim);
	};

	const stopAnimation = () => {
		const currentAnim = animatedElements.get(element);
		if (currentAnim) {
			currentAnim.pause();
		}
	};

	observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					startAnimation();
				} else {
					stopAnimation();
				}
			});
		},
		{ threshold: 0.1 },
	);
	observer.observe(element);

	element._rotateObserver = observer;
};

// (stopRotateAnim removed; use stopManagedAnimation from timing.js directly if needed)

// Editor preview helper (single one-off rotate regardless of loop settings)
// Keeps editor preview logic centralized with animation definitions.
export const rotatePreview = (
	element,
	{ gsap, duration = 2, ease = "power2.inOut", from = 0, to = 360, transformOrigin = "center center" } = {},
) => {
	if (!element || !gsap) return null;
	return gsap.fromTo(element, { rotation: from }, { rotation: to, duration, ease: mapEase(ease), transformOrigin });
};
